// OMG-2629. Every Contentful image the members app renders goes through this
// helper. Rendering `fields.file.url` bare ships the uploaded original:
// 2000x2000 date-night JPEGs of up to 3.9 MB and 1920x1080 video posters of up
// to 2.2 MB, which were 261 GB of one 557 GB billing cycle (OMG-2567). With
// `w`, `fm=webp` and `q=75` the same images come back 96 to 99% smaller.
//
// `w` is a cap, not a target: Contentful never upscales on `w` (or `w` + `h`)
// alone, so a source narrower than `w` comes back at its own width. That stops
// being true once `fit` joins `w` + `h`: fill, scale and thumb return the whole
// box (2.3x the bytes, measured) and pad 4.8x. So this helper never sets `fit`,
// and drops one it is handed (and `fl`, which breaks once `fm` changes).
//
// Only images.ctfassets.net is an Images API host. Anything else (videos.,
// assets., downloads.ctfassets.net, local paths) is returned untouched, as are
// SVGs (`fm` rasterises them) and GIFs (`fm` drops the animation).
// eslint.config.mjs rejects a raw `fields.file.url` in a src, srcSet, poster or
// CSS url(...) so new call sites cannot skip the helper.

const IMAGES_API_HOST = "images.ctfassets.net";
const PASSTHROUGH_PATH = /\.(svg|gif)$/i;
// Contentful answers w or h above 4000 with a 400, which is a broken image.
const MAX_DIMENSION = 4000;

export type ContentfulImageFormat = "webp" | "avif" | "jpg";

export interface ContentfulImageOptions {
  /** Width cap in px. Size it at about 2x the widest CSS width of the slot. */
  w: number;
  /** Optional height cap. Without `fit` the image scales to fit inside w x h. */
  h?: number;
  q?: number;
  fm?: ContentfulImageFormat;
}

const dimension = (value: number) =>
  String(Math.min(Math.round(value), MAX_DIMENSION));

export function contentfulImage(
  url: string | undefined,
  { w, h, q = 75, fm = "webp" }: ContentfulImageOptions,
): string | undefined {
  if (!url) return url;

  // Contentful's `file.url` is protocol-relative (`//images.ctfassets.net/...`).
  const protocolRelative = url.startsWith("//");
  let parsed: URL;
  try {
    parsed = new URL(protocolRelative ? `https:${url}` : url);
  } catch {
    return url;
  }

  if (
    parsed.hostname !== IMAGES_API_HOST ||
    PASSTHROUGH_PATH.test(parsed.pathname)
  ) {
    return url;
  }

  const params = parsed.searchParams;
  params.delete("fit");
  // `fl` is format-specific (progressive is jpg-only, png8 png-only), and
  // Contentful answers it next to a different `fm` with a 400.
  params.delete("fl");
  params.set("w", dimension(w));
  if (h !== undefined) params.set("h", dimension(h));
  params.set("fm", fm);
  params.set("q", String(q));

  const result = parsed.toString();
  return protocolRelative ? result.slice("https:".length) : result;
}
